/**
 * Turns model output into insights-leader language: which levers matter,
 * where the risks are, what differs per audience, and what to validate with
 * real consumers next. Everything is derived from `predict`/`optimize`, so it
 * follows the configuration the user is exploring.
 */

import { applyFilters, DEFAULT_FILTERS } from "./filters";
import { AUDIENCES, FACTORS, optimize, predict } from "./model";
import { stateResults } from "./regions";
import {
  AudienceId,
  Configuration,
  FactorId,
  Filters,
  Finding,
  Kpis,
  NextStep,
} from "./types";
import { SAMPLE_SIZE, shareAcceptingPrice, verbatims } from "./voice";

/* ── Types ─────────────────────────────────────── */

export interface AudienceSummary {
  /** True when brand fit is below the risk threshold. */
  brandRisk: boolean;
  /** Purchase-intent gap vs. national, in points. */
  gap: number;
  kpis: Kpis;
  /** Single switch with the biggest purchase-intent gain, if any. */
  lever: Improvement | null;
}

export interface InsightSummary {
  /** Body copy; `**text**` marks terms to highlight. */
  body: string;
  headline: string;
  /** Where each number comes from, in body order. */
  sources: string[];
}

export interface Improvement {
  factor: FactorId;
  factorLabel: string;
  gain: number;
  optionLabel: string;
  value: string;
}

export interface FactorSensitivity {
  best: { label: string; value: string; kpis: Kpis };
  factor: FactorId;
  label: string;
  /** Purchase-intent range across options, in points. */
  range: number;
  worst: { label: string; value: string; kpis: Kpis };
}

/* ── Constants ─────────────────────────────────── */

const BRAND_FIT_THRESHOLD = 62;

const CLOSE_CALL_MARGIN = 3;

const METHODS: Record<FactorId, { method: string; verb: string }> = {
  advertising: {
    method: "Ad creative test · n=300 per cell",
    verb: "Compare media plans",
  },
  claim: { method: "Claim test (MaxDiff) · n=400", verb: "Test claims" },
  packSize: { method: "Preference test · n=300", verb: "Compare formats" },
  packaging: {
    method: "Packaging test · n=300 per cell",
    verb: "Compare packs",
  },
  positioning: {
    method: "Monadic concept test · n=300 per cell",
    verb: "Compare campaign ideas",
  },
  price: {
    method: "Price elasticity (Gabor-Granger) · n=500",
    verb: "Explore price sensitivity",
  },
  retail: {
    method: "Awareness & channel survey · n=500",
    verb: "Validate channel",
  },
};

const NEXT_STEP_COUNT = 4;

/* ── Public API ─────────────────────────────────── */

/** Findings for one audience, relative to the national ("all") picture. */
export const audienceFindings = (
  configuration: Configuration,
  audience: AudienceId
): Finding[] => {
  const kpis = predict(configuration, audience);
  const national = predict(configuration, "all");
  const label = audienceLabel(audience);
  const findings: Finding[] = [];

  if (audience !== "all") {
    const gap = kpis.purchaseIntent - national.purchaseIntent;
    findings.push({
      detail: `${pct(kpis.purchaseIntent)} purchase intent vs. ${pct(national.purchaseIntent)} nationally · WTP ${usd(kpis.wtp)} · brand fit ${Math.round(kpis.brandFit)}/100.`,
      evidenceKpi: "purchaseIntent",
      headline:
        Math.abs(gap) < 2
          ? `${label} react in line with the national average`
          : gap > 0
            ? `${label} over-index by ${range(gap)} on this configuration`
            : `${label} under-index by ${range(-gap)} on this configuration`,
      quote: verbatims("purchaseIntent", configuration, audience, kpis, 3)[0],
      tone: gap >= 2 ? "opportunity" : gap <= -2 ? "risk" : "neutral",
    });
  }

  const result = optimize(audience, configuration);
  const gains = improvements(configuration, audience);
  if (gains.length > 0) {
    const top = gains.slice(0, 2);
    const optimal = predict(result.configuration, audience);
    findings.push({
      detail: `Biggest single moves: ${top
        .map((g) => `${g.factorLabel} → ${g.optionLabel} (${pts(g.gain)})`)
        .join(
          ", "
        )}. Their own optimum lands at ${pct(optimal.purchaseIntent)} intent with a ${usd(optimal.wtp)} acceptable price.`,
      evidenceKpi: "purchaseIntent",
      headline: `${top[0].optionLabel} is the ${top[0].factorLabel.toLowerCase()} ${label} respond to most`,
      tone: "opportunity",
    });
  } else {
    findings.push({
      detail: `This configuration is already the #1 of ${result.scenarioCount.toLocaleString("en-US")} scenarios for ${label}.`,
      evidenceKpi: "purchaseIntent",
      headline: `Current configuration is optimal for ${label}`,
      tone: "opportunity",
    });
  }

  const risk = brandRisk(configuration, audience, kpis);
  if (risk) findings.push(risk);

  return findings;
};

/**
 * Compact per-audience picture for side-by-side comparison. Each audience is
 * read at its own optimum over the full scenario space, so the card does not
 * move with the knobs; demographic filters narrow the population.
 */
export const audienceSummary = (
  audience: AudienceId,
  filters: Filters = DEFAULT_FILTERS
): AudienceSummary => {
  const own = optimize(audience);
  const national = optimize("all");
  const kpis = applyFilters(own.kpis, own.configuration, filters);
  const nationalKpis = applyFilters(
    national.kpis,
    national.configuration,
    filters
  );
  const strongest = sensitivity(own.configuration, audience)[0];
  return {
    brandRisk: kpis.brandFit < BRAND_FIT_THRESHOLD,
    gap: kpis.purchaseIntent - nationalKpis.purchaseIntent,
    kpis,
    lever: {
      factor: strongest.factor,
      factorLabel: strongest.label,
      gain: strongest.range,
      optionLabel: strongest.best.label,
      value: strongest.best.value,
    },
  };
};

/**
 * Key findings for one audience across every scenario: the picture is read at
 * the audience's optimum (the best of all configurations), so it depends on
 * the audience, not on the knobs currently set.
 */
export const globalFindings = (audience: AudienceId): Finding[] => {
  const { configuration, kpis } = optimize(audience);
  const levers = sensitivity(configuration, audience);
  const scope = audience === "all" ? "consumers" : audienceLabel(audience);
  const findings: Finding[] = [];

  const [first, second] = levers;
  findings.push({
    detail: `Purchase intent ranges from ${pct(first.worst.kpis.purchaseIntent)} (${first.worst.label}) to ${pct(first.best.kpis.purchaseIntent)} (${first.best.label}), a swing of ${range(first.range)}. ${second.label} is the next lever at ${range(second.range)}.`,
    evidenceKpi: "purchaseIntent",
    headline: `${first.label} is the strongest lever for ${scope}`,
    quote: verbatims("purchaseIntent", configuration, audience, kpis, 4).find(
      (v) => v.sentiment !== "neutral"
    ),
    tone: "neutral",
  });

  const price = Number(configuration.price);
  const accepting = shareAcceptingPrice(kpis.wtp, price);
  const priceOk = accepting >= 50;
  findings.push({
    detail: `${usd(price)} is the price point that wins across all scenarios for ${scope}. ${Math.round(accepting)}% accept it or more; the average acceptable price is ${usd(kpis.wtp)}. ${
      priceOk
        ? kpis.wtp >= price
          ? "There is headroom: a higher price point is worth testing."
          : "A majority accepts the shelf price, but the average sits just below it: little room to go higher."
        : "The shelf price sits above what most buyers would pay."
    }`,
    evidenceKpi: "wtp",
    headline: priceOk
      ? `${usd(price)} is accepted by a majority of ${scope}`
      : `${usd(price)} is above what most ${scope} accept`,
    quote: verbatims("wtp", configuration, audience, kpis, 4).find(
      (v) => v.sentiment === (priceOk ? "positive" : "negative")
    ),
    tone: priceOk ? (kpis.wtp >= price ? "opportunity" : "neutral") : "risk",
  });

  const spread = audienceSpread();
  findings.push({
    detail: `${spread.top.label} lead at ${pct(spread.top.kpis.purchaseIntent)} intent at their own optimum; ${spread.bottom.label} trail at ${pct(spread.bottom.kpis.purchaseIntent)}. ${
      spread.differ
        ? `They also want different things: ${spread.differ}.`
        : "The same configuration works for both."
    }`,
    evidenceKpi: "purchaseIntent",
    headline: `Audiences are ${range(spread.top.kpis.purchaseIntent - spread.bottom.kpis.purchaseIntent)} apart on purchase intent`,
    tone: "neutral",
  });

  const riskAudience = audience === "all" ? "nutellaBuyers" : audience;
  const risk = brandRisk(
    configuration,
    riskAudience,
    predict(configuration, riskAudience)
  );
  if (risk) findings.push(risk);

  const weakest = levers[levers.length - 1];
  findings.push({
    detail: `Across its options ${weakest.label.toLowerCase()} moves intent by at most ${range(weakest.range)}. Decide it on cost and operations, not on consumer response.`,
    evidenceKpi: "purchaseIntent",
    headline: `${weakest.label} barely moves ${scope}`,
    tone: "neutral",
  });

  const regional = stateResults(configuration, kpis, audience)
    .map((r) => ({
      delta: r.kpis.purchaseIntent - kpis.purchaseIntent,
      name: r.state.name,
    }))
    .sort((a, b) => b.delta - a.delta);
  findings.push({
    detail: `Strongest: ${regional
      .slice(0, 3)
      .map((r) => `${r.name} (${pts(r.delta)})`)
      .join(", ")}. Weakest: ${regional
      .slice(-2)
      .map((r) => `${r.name} (${pts(r.delta)})`)
      .join(
        ", "
      )}. A regional pilot would start where the winning configuration already over-indexes.`,
    evidenceKpi: "purchaseIntent",
    headline: `Reacts best in ${regional[0].name}, ${regional[1].name} and ${regional[2].name}`,
    tone: "neutral",
  });

  return findings;
};

/** Headline + body for the Key Insights card of one audience. */
export const insightSummary = (
  configuration: Configuration,
  audience: AudienceId
): InsightSummary => {
  const kpis = predict(configuration, audience);
  const national = predict(configuration, "all");
  const levers = sensitivity(configuration, audience);
  const strongest = levers[0];
  const weakest = levers[levers.length - 1];
  const price = Number(configuration.price);
  const accepting = Math.round(shareAcceptingPrice(kpis.wtp, price));
  const positioning = optionLabel("positioning", configuration.positioning);
  const gap = kpis.purchaseIntent - national.purchaseIntent;

  const headline =
    audience === "all"
      ? `${pct(kpis.purchaseIntent)} of US consumers would buy Nutella Peanut Butter at ${usd(price)} with the “${positioning}” positioning.`
      : `${audienceLabel(audience)} respond best to the “${positioning}” positioning at ${usd(price)}: ${pct(kpis.purchaseIntent)} purchase intent, ${pts(gap)} vs. the national average.`;

  const scope = audience === "all" ? "US consumers" : audienceLabel(audience);
  const body = [
    `**${strongest.label}** is the strongest lever for ${scope}: switching from “${strongest.worst.label}” to “${strongest.best.label}” moves purchase intent by **${range(strongest.range)}**. ${weakest.label} barely registers (${range(weakest.range)}).`,
    `**${accepting}%** of ${scope} accept ${usd(price)} or more; their average acceptable price is **${usd(kpis.wtp)}**${
      kpis.wtp >= price
        ? ", leaving room to test a higher shelf price."
        : ", just under the shelf price."
    }`,
    kpis.brandFit >= BRAND_FIT_THRESHOLD
      ? `Brand fit at **${Math.round(kpis.brandFit)}/100** keeps the product inside the Nutella franchise.`
      : `Brand fit at **${Math.round(kpis.brandFit)}/100** is below the ${BRAND_FIT_THRESHOLD} comfort line: the concept borrows Nutella equity it may not give back.`,
  ].join(" ");

  const sources = [
    `Purchase-intent question, ${scope}, n = ${SAMPLE_SIZE}, one-factor-at-a-time re-simulation`,
    `Maximum acceptable price (Van Westendorp-style), ${scope}, n = ${SAMPLE_SIZE}`,
    `Brand-fit rating, ${scope}, n = ${SAMPLE_SIZE}`,
  ];

  return { body, headline, sources };
};

/**
 * Tests worth running with real consumers: the factors where two options are
 * within a few purchase-intent points of each other, so a real-world read has
 * the highest information value before committing.
 */
export const nextSteps = (): NextStep[] => {
  const candidates: (NextStep & { score: number })[] = [];

  for (const audience of AUDIENCES.map((a) => a.id)) {
    // Close calls are read around each audience's own optimum, i.e. the best
    // of every scenario, so the list does not depend on the knobs set.
    const configuration = optimize(audience).configuration;
    for (const factor of FACTORS) {
      const ranked = factor.options
        .map((option) => ({
          label: option.label,
          pi: predict({ ...configuration, [factor.id]: option.value }, audience)
            .purchaseIntent,
        }))
        .sort((a, b) => b.pi - a.pi);
      const [best, runnerUp] = ranked;
      const margin = best.pi - runnerUp.pi;
      if (margin > CLOSE_CALL_MARGIN) continue;
      const meta = METHODS[factor.id];
      const title =
        factor.id === "price"
          ? `Explore price sensitivity between ${cheaper(best.label, runnerUp.label)} and ${dearer(best.label, runnerUp.label)}`
          : `${meta.verb}: ${best.label} vs. ${runnerUp.label}`;
      candidates.push({
        audience,
        detail:
          margin < 0.5
            ? `${best.label} and ${runnerUp.label} land on the same purchase intent for ${audienceLabel(audience)} (${pct(best.pi)}). The choice comes down to execution, so a real-world read is the cheapest way to lock it.`
            : `${best.label} leads ${runnerUp.label} by ${marginPts(margin)} of purchase intent for ${audienceLabel(audience)} (${pct(best.pi)} vs. ${pct(runnerUp.pi)}). ${meta.method.split(" · ")[0]} with real consumers would confirm the lead before committing.`,
        factor: factor.id,
        margin,
        method: meta.method,
        // Close calls on strong levers matter most.
        score:
          (ranked[0].pi - ranked[ranked.length - 1].pi) /
          (margin + 0.5) /
          (audience === "all" ? 0.7 : 1),
        title,
      });
    }
  }

  const seen = new Set<string>();
  return candidates
    .sort((a, b) => b.score - a.score)
    .filter((c) => {
      if (seen.has(c.factor)) return false;
      seen.add(c.factor);
      return true;
    })
    .slice(0, NEXT_STEP_COUNT)
    .map(({ score: _score, ...step }) => step);
};

/** One-at-a-time sensitivity of purchase intent to each factor, strongest first. */
export const sensitivity = (
  configuration: Configuration,
  audience: AudienceId
): FactorSensitivity[] =>
  FACTORS.map((factor) => {
    const scored = factor.options.map((option) => ({
      kpis: predict({ ...configuration, [factor.id]: option.value }, audience),
      label: option.label,
      value: option.value,
    }));
    const sorted = [...scored].sort(
      (a, b) => b.kpis.purchaseIntent - a.kpis.purchaseIntent
    );
    return {
      best: sorted[0],
      factor: factor.id,
      label: factor.label,
      range:
        sorted[0].kpis.purchaseIntent -
        sorted[sorted.length - 1].kpis.purchaseIntent,
      worst: sorted[sorted.length - 1],
    };
  }).sort((a, b) => b.range - a.range);

/* ── Private helpers ────────────────────────────── */

const audienceLabel = (audience: AudienceId): string =>
  AUDIENCES.find((a) => a.id === audience)?.label ?? "All US consumers";

/** Most and least receptive audiences at their own optima, and how those differ. */
const audienceSpread = () => {
  const ranked = AUDIENCES.filter((a) => a.id !== "all")
    .map((a) => ({ ...a, kpis: optimize(a.id).kpis }))
    .sort((a, b) => b.kpis.purchaseIntent - a.kpis.purchaseIntent);
  const top = ranked[0];
  const bottom = ranked[ranked.length - 1];
  const topOptimum = optimize(top.id).configuration;
  const bottomOptimum = optimize(bottom.id).configuration;
  const differ = FACTORS.filter((f) => topOptimum[f.id] !== bottomOptimum[f.id])
    .slice(0, 2)
    .map(
      (f) =>
        `${f.label.toLowerCase()} (${optionLabel(f.id, topOptimum[f.id])} vs. ${optionLabel(f.id, bottomOptimum[f.id])})`
    )
    .join(", ");
  return { bottom, differ, top };
};

const brandRisk = (
  configuration: Configuration,
  audience: AudienceId,
  kpis: Kpis
): Finding | null => {
  if (kpis.brandFit >= BRAND_FIT_THRESHOLD) return null;
  // Factors where another option would lift brand fit by 3+ points.
  const culprits = FACTORS.map((f) => ({
    factor: f,
    lift: Math.max(
      ...f.options.map(
        (o) =>
          predict({ ...configuration, [f.id]: o.value }, audience).brandFit -
          kpis.brandFit
      )
    ),
  }))
    .filter((c) => c.lift >= 3)
    .sort((a, b) => b.lift - a.lift)
    .slice(0, 2)
    .map(
      (c) =>
        `${c.factor.label.toLowerCase()} (${optionLabel(c.factor.id, configuration[c.factor.id])})`
    );
  return {
    detail: `Brand fit is ${Math.round(kpis.brandFit)}/100 for ${audienceLabel(audience)}${
      culprits.length > 0
        ? `; ${culprits.join(" and ")} weaken the Nutella link`
        : ""
    }. Below ~${BRAND_FIT_THRESHOLD} the launch starts borrowing equity it may not give back.`,
    evidenceKpi: "brandFit",
    headline: `Brand fit risk with ${audienceLabel(audience)}`,
    quote: verbatims("brandFit", configuration, audience, kpis, 5).find(
      (v) => v.sentiment === "negative"
    ),
    tone: "risk",
  };
};

const cheaper = (a: string, b: string) =>
  parseFloat(a.slice(1)) <= parseFloat(b.slice(1)) ? a : b;
const dearer = (a: string, b: string) =>
  parseFloat(a.slice(1)) > parseFloat(b.slice(1)) ? a : b;

/** Single-factor switches towards the audience optimum, biggest PI gain first. */
const improvements = (
  configuration: Configuration,
  audience: AudienceId
): Improvement[] => {
  const optimum = optimize(audience, configuration).configuration;
  const base = predict(configuration, audience).purchaseIntent;
  return FACTORS.filter((f) => optimum[f.id] !== configuration[f.id])
    .map((f) => ({
      factor: f.id,
      factorLabel: f.label,
      gain:
        predict({ ...configuration, [f.id]: optimum[f.id] }, audience)
          .purchaseIntent - base,
      optionLabel: optionLabel(f.id, optimum[f.id]),
      value: optimum[f.id],
    }))
    .filter((g) => g.gain >= 0.5)
    .sort((a, b) => b.gain - a.gain);
};

const optionLabel = (factor: FactorId, value: string): string =>
  FACTORS.find((f) => f.id === factor)?.options.find((o) => o.value === value)
    ?.label ?? value;

const marginPts = (value: number) => {
  const text = value.toFixed(1);
  return `${text}\u00A0pt${text === "1.0" ? "" : "s"}`;
};
const pct = (value: number) => `${Math.round(value)}%`;
const pts = (value: number) => {
  const rounded = Math.round(value);
  if (rounded === 0) return "0\u00A0pts";
  return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded)}\u00A0pt${Math.abs(rounded) === 1 ? "" : "s"}`;
};
const range = (value: number) => {
  const rounded = Math.round(value);
  return `${rounded}\u00A0pt${rounded === 1 ? "" : "s"}`;
};
const usd = (value: number) => `$${value.toFixed(2)}`;
